import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, of, interval, Subscription } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { TokenStorageService } from './token-storage.service';
import { AuthData } from '../model/session';
import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/api/api-response.model';
import { RegisterResponse, RegisterUserDto } from '../model/auth';
import { ToastService } from './toast.service';
import { UserStatus } from 'src/app/core/enum/user-status';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private baseUrl = environment.apiUrl;
  private refreshSub?: Subscription;
  private readonly REFRESH_INTERVAL = environment.REFRESH_INTERVAL_MINUTES * 60 * 1000;
  private refreshRequest$: Observable<string> | null = null;

  constructor(
    private http: HttpClient,
    private tokenStorage: TokenStorageService,
    private toast: ToastService
  ) {}

  login(
    email: string,
    password: string,
    recaptchaToken: string
  ): Observable<ApiResponse<AuthData>> {
    const body = {
      email,
      password,
      'g-recaptcha-response': recaptchaToken,
    };

    return this.http
      .post<ApiResponse<AuthData>>(`${this.baseUrl}/login`, body)
      .pipe(
        tap((res: ApiResponse<AuthData>) => {
          this.tokenStorage.setAuth(res.dto);
        }),
      );
  }

  logout(): Observable<any> {
    this.stopAutoRefresh();
    const token = this.getToken();
    if (!token) {
      this.tokenStorage.clear();
      return of(null);
    }
    return this.http
      .post<ApiResponse<null>>(`${this.baseUrl}/logout`, {})
      .pipe(finalize(() => this.tokenStorage.clear()));
  }

  refreshToken(): Observable<string> {
    const refreshToken = this.tokenStorage.getRefreshToken();

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const req$ = this.http
      .post<ApiResponse<AuthData>>(`${this.baseUrl}/refresh`, { refreshToken })
      .pipe(
        map((res) => {
          const newAccess = res.dto?.token ?? '';
          const newRefresh = res.dto?.refreshToken ?? undefined;
          const current = this.tokenStorage.getAuth();

          if (current) {
            const updated: AuthData = {
              ...current,
              token: newAccess,
              ...(newRefresh ? { refreshToken: newRefresh } : {}),
            };
            this.tokenStorage.setAuth(updated);
          } else {
            const newAuth: AuthData = {
              token: newAccess,
              ...(newRefresh ? { refreshToken: newRefresh } : {}),
            };
            this.tokenStorage.setAuth(newAuth);
          }

          return newAccess;
        }),
        catchError((err) => {
          this.toast.showMessages('error', [
            'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
          ]);
          this.clear();
          this.stopAutoRefresh();
          return throwError(() => err);
        }),
        finalize(() => {
          this.refreshRequest$ = null;
        }),
        shareReplay(1)
      );

    this.refreshRequest$ = req$;
    return req$;
  }

  private startAutoRefresh() {
    this.stopAutoRefresh();
    this.refreshSub = interval(this.REFRESH_INTERVAL).subscribe(() => {
      this.refreshToken().subscribe({
        next: () => {},
      });
    });
  }

  private stopAutoRefresh() {
    if (this.refreshSub) {
      this.refreshSub.unsubscribe();
      this.refreshSub = undefined;
    }
  }

  getToken(): string | null {
    return this.tokenStorage.getToken();
  }

  getRefreshToken(): string | null {
    return this.tokenStorage.getRefreshToken();
  }

  clear(): void {
    this.tokenStorage.clear();
  }

  setProfileCompleted(): void {
    const current = this.tokenStorage.getAuth();
    if (current) {
      const updated: AuthData = {
        ...current,
        status: UserStatus.AUTHENTICATED,
      };
      this.tokenStorage.setAuth(updated);
    }
  }

  register(user: RegisterUserDto): Observable<RegisterResponse> {
    const url = `${this.baseUrl}/usuario`;
    return this.http.post<RegisterResponse>(url, user);
  }

  completeRegistration(data: {
    identificationDocument: string;
    sex: 'Masculino' | 'Femenino' | 'Indistinto';
  }): Observable<any> {
    const url = `${this.baseUrl}/usuario/completar-registro`;
    return this.http.patch<ApiResponse<any>>(url, data);
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }
}
